import i18n from "@/i18n.ts";
import { SettingGroup, SettingItem } from "@/pages/settings/index/settings.types.ts";
import { ApiMeEmployee } from "@/types/employees/employee.types.ts";
import { ApiPermission, PermissionType } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";

interface ReportItem extends Omit<SettingItem, "permission"> {
  permission: ApiPermission;
  feature?: ApiFeatureType;
}

interface ReportGroup extends Omit<SettingGroup, "items"> {
  items: ReportItem[];
}

export const reportingModulePermissions: ApiPermission[] = [
  ApiPermission.reporting_recruiting_stage_flow,
  ApiPermission.reporting_recruiting_closing_time,
  ApiPermission.reporting_recruiting_source_efficiency,
  ApiPermission.reporting_recruiting_disqualification_reasons,
  ApiPermission.reporting_employee_headcount,
  ApiPermission.reporting_employee_salary,
  ApiPermission.reporting_employee_retention,
  ApiPermission.reporting_people_analytics,
];

export const ReportingConfig = () => {
  return [
    {
      title: i18n.t("ats:recruit"),
      items: [
        {
          title: i18n.t("ats:report_hiring_pipeline"),
          menuTitle: i18n.t("ats:report_hiring_pipeline"),
          description: i18n.t("ats:report_hiring_pipeline_description"),
          to: "/reporting/recruiting/stage-flow-report",
          permission: ApiPermission.reporting_recruiting_stage_flow,
          icon: "fa-chart-pie",
          feature: ApiFeatureType.ats,
        },
        {
          title: i18n.t("ats:report_source_efficiency"),
          menuTitle: i18n.t("ats:report_source_efficiency"),
          description: i18n.t("ats:report_source_efficiency_table_title"),
          to: "/reporting/recruiting/source-efficiency-report",
          permission: ApiPermission.reporting_recruiting_source_efficiency,
          icon: "fa-chart-pie",
          feature: ApiFeatureType.ats,
        },
        {
          title: i18n.t("ats:report_closing_time"),
          menuTitle: i18n.t("ats:report_closing_time"),
          description: i18n.t("ats:report_closing_time_description"),
          to: "/reporting/recruiting/closing-time-report",
          permission: ApiPermission.reporting_recruiting_closing_time,
          icon: "fa-chart-pie",
          feature: ApiFeatureType.ats,
        },
        {
          title: i18n.t("ats:report_disqualification_reasons"),
          menuTitle: i18n.t("ats:report_disqualification_reasons"),
          description: i18n.t("ats:report_disqualification_reasons_description"),
          to: "/reporting/recruiting/disqualification-reasons-report",
          permission: ApiPermission.reporting_recruiting_disqualification_reasons,
          icon: "fa-chart-pie",
          feature: ApiFeatureType.ats,
        },
      ],
    },
    {
      title: i18n.t("core:team"),
      items: [
        {
          title: i18n.t("reporting:headcount_report_title"),
          menuTitle: i18n.t("reporting:headcount_report_title"),
          description: i18n.t("reporting:headcount_report_description"),
          to: "/reporting/employee/headcount-report-analytics",
          permission: ApiPermission.reporting_employee_headcount,
          icon: "fa-chart-pie",
        },
        {
          title: i18n.t("reporting:retention_report_title"),
          menuTitle: i18n.t("reporting:retention_report_title"),
          description: i18n.t("reporting:retention_report_description"),
          to: "/reporting/employee/retention-report-analytics",
          permission: ApiPermission.reporting_employee_retention,
          icon: "fa-chart-pie",
        },
        {
          title: i18n.t("reporting:salary_report_title"),
          menuTitle: i18n.t("reporting:salary_report_title"),
          description: i18n.t("reporting:salary_report_description"),
          to: "/reporting/employee/salary-report-analytics",
          permission: ApiPermission.reporting_employee_salary,
          icon: "fa-chart-pie",
          isBeta: true,
        },
        {
          title: i18n.t("reporting:people_analytics_title"),
          menuTitle: i18n.t("reporting:people_analytics_title"),
          description: i18n.t("reporting:people_analytics_description"),
          to: "/reporting/employee/people-analytics",
          permission: ApiPermission.reporting_people_analytics,
          icon: "fa-chart-pie",
        },
      ],
    },
  ] as ReportGroup[];
};

export const getReportsVisibleToEmployee = (employee: ApiMeEmployee): ReportGroup[] => {
  const result: ReportGroup[] = [];
  const reportsConfig = ReportingConfig();

  for (let groupIndex = 0; groupIndex < reportsConfig.length; groupIndex++) {
    const showItems: ReportItem[] = [];

    for (const reportItem of reportsConfig[groupIndex].items) {
      const show = employee.api_permissions[reportItem.permission] === PermissionType.edit;
      const hasFeature = reportItem.feature
        ? employee.plan.features[reportItem.feature].enabled
        : true;

      if (show && hasFeature) showItems.push(reportItem);
    }

    if (showItems.length > 0) {
      result.push({
        title: reportsConfig[groupIndex].title,
        items: showItems,
      });
    }
  }
  return result;
};

export const hasReportsAccessForEmployee = (employee: ApiMeEmployee): boolean => {
  return getReportsVisibleToEmployee(employee).length > 0;
};
